/**
 * @module retry
 * @description Provides retry helpers for transient adapter failures. The
 * module applies exponential backoff and delegates retry eligibility to the
 * shared typed error helpers so callers can centralize retry policy.
 */

import { AdapterConfigurationError, isRetryable } from "./errors";

/**
 * Configuration options for automatic retry behavior.
 * All options are optional and have sensible defaults.
 */
export interface RetryOptions {
  /** Maximum number of attempts to make (default: 3, meaning 1 initial + 2 retries) */
  maxAttempts?: number;
  /** Initial delay before first retry in milliseconds (default: 500ms) */
  delayMs?: number;
  /** Multiplier applied to delay after each failed attempt (default: 2, creating exponential backoff) */
  backoffMultiplier?: number;
  /** Maximum delay between attempts in milliseconds. Defaults to no cap. */
  maxDelayMs?: number;
  /** Custom function to determine if an error should be retried (default: isRetryable from errors module) */
  shouldRetry?: (error: unknown) => boolean;
}

function throwInvalidRetryOption(message: string): never {
  throw new AdapterConfigurationError(message, "INVALID_CONFIG");
}

function validateRetryOptions(
  maxAttempts: number,
  delayMs: number,
  backoffMultiplier: number,
  maxDelayMs: number,
): void {
  if (!Number.isInteger(maxAttempts) || maxAttempts < 1) {
    throwInvalidRetryOption("retry maxAttempts must be an integer >= 1");
  }
  if (!Number.isFinite(delayMs) || delayMs < 0) {
    throwInvalidRetryOption("retry delayMs must be a finite number >= 0");
  }
  if (!Number.isFinite(backoffMultiplier) || backoffMultiplier <= 0) {
    throwInvalidRetryOption(
      "retry backoffMultiplier must be a finite number > 0",
    );
  }
  if (
    maxDelayMs !== Number.POSITIVE_INFINITY &&
    (!Number.isFinite(maxDelayMs) || maxDelayMs < 0)
  ) {
    throwInvalidRetryOption(
      "retry maxDelayMs must be a finite number >= 0 or Infinity",
    );
  }
}

/**
 * Executes an async function with automatic retry logic.
 * Implements exponential backoff to handle transient failures gracefully.
 *
 * @typeParam T - The return type of the function being retried
 * @param fn - The async function to execute and potentially retry
 * @param options - Configuration options for retry behavior
 * @returns A promise that resolves to the function's return value
 * @throws {Error} The last error encountered after all retry attempts are exhausted
 *
 * @example
 * ```ts
 * // Retry with defaults (3 attempts, 500ms initial delay, 2x backoff)
 * const user = await withRetry(() => adapter.db.getUser('123'));
 *
 * // Customize retry behavior
 * const result = await withRetry(
 *   () => adapter.ai.chat(messages),
 *   {
 *     maxAttempts: 5,
 *     delayMs: 1000,
 *     backoffMultiplier: 1.5,
 *     maxDelayMs: 5000,
 *   }
 * );
 *
 * // Custom retry predicate - only retry specific errors
 * const data = await withRetry(
 *   () => expensiveOperation(),
 *   {
 *     shouldRetry: (error) => {
 *       // Only retry if it's a network error or rate limit
 *       return error.code === 'RATE_LIMIT' || error.code === 'CONNECTION_ERROR';
 *     }
 *   }
 * );
 * ```
 *
 * @remarks
 * The delay progression with default settings (maxAttempts=3, delayMs=500, backoffMultiplier=2):
 * - Attempt 1: Immediate
 * - Attempt 2: After 500ms
 * - Attempt 3: After 1000ms
 *
 * For custom shouldRetry: If false is returned, the error is thrown immediately without further retries.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const {
    maxAttempts = 3,
    delayMs = 500,
    backoffMultiplier = 2,
    maxDelayMs = Number.POSITIVE_INFINITY,
    shouldRetry = isRetryable,
  } = options;

  validateRetryOptions(maxAttempts, delayMs, backoffMultiplier, maxDelayMs);

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      // Attempt to execute the function
      return await fn();
    } catch (error) {
      lastError = error;

      // Check if this is the last attempt or if this error type is not retryable
      if (attempt === maxAttempts || !shouldRetry(error)) {
        throw error;
      }

      // Calculate the delay for this retry using exponential backoff
      // Formula: delayMs * (backoffMultiplier ^ (attemptNumber - 1))
      const delay = Math.min(
        delayMs * backoffMultiplier ** (attempt - 1),
        maxDelayMs,
      );

      // Wait before retrying
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // This should never be reached, but throw lastError for defensive fallback.
  throw lastError;
}
